import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSignMessage } from 'wagmi'
import { db, getSupabaseClient } from '@/services/index.ts'
import { getFriendlyTransactionError } from '@/utils/walletError.ts'
import { useWallet } from '@/hooks/useWallet.ts'
import type { Profile } from '@/hooks/useProfile.ts'

const PAGE_SIZE = 10

export function AdminUserManagement() {
  const { t } = useTranslation()
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [filter, setFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editEmail, setEditEmail] = useState('')
  const [editPhone, setEditPhone] = useState('')
  const [saving, setSaving] = useState(false)
  const [claiming, setClaiming] = useState(false)
  const [claimError, setClaimError] = useState<string | null>(null)
  const { address } = useWallet()
  const { signMessageAsync } = useSignMessage()

  const loadCount = useCallback(async (search: string) => {
    const res = await db.count(
      'profiles',
      search
        ? [{ column: 'email', operator: 'ilike', value: `%${search}%` }]
        : undefined,
    )
    if (res.error) return
    setTotal(res.data ?? 0)
  }, [])

  const loadPage = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data, error: err } = await db.findMany<Profile>('profiles', {
        filters: filter
          ? [{ column: 'email', operator: 'ilike', value: `%${filter}%` }]
          : undefined,
        orderBy: { column: 'created_at', ascending: false },
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      })
      if (err) {
        setError(err.message)
        setProfiles([])
      } else {
        setProfiles(data ?? [])
      }
      await loadCount(filter)
    } finally {
      setLoading(false)
    }
  }, [page, filter, loadCount])

  useEffect(() => {
    loadPage()
  }, [loadPage])

  useEffect(() => {
    setPage(0)
  }, [filter])

  const startEdit = (p: Profile) => {
    setEditingId(p.id)
    setEditEmail(p.email)
    setEditPhone(p.phone ?? '')
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditEmail('')
    setEditPhone('')
  }

  const saveEdit = async () => {
    if (!editingId) return
    setSaving(true)
    try {
      const { error: err } = await db.updateById<Profile>(
        'profiles',
        editingId,
        {
          email: editEmail,
          phone: editPhone || null,
          updated_at: new Date().toISOString(),
        },
      )
      if (err) {
        setError(err.message)
      } else {
        setProfiles((prev) =>
          prev.map((p) =>
            p.id === editingId
              ? { ...p, email: editEmail, phone: editPhone || null }
              : p,
          ),
        )
        cancelEdit()
      }
    } finally {
      setSaving(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const claimAdminSession = useCallback(async () => {
    if (!address || !signMessageAsync) return
    setClaiming(true)
    setClaimError(null)
    try {
      const message = `BitsLegacy Admin Login at ${Date.now()}`
      const signature = await signMessageAsync({ message })
      const supabase = getSupabaseClient()
      const { data, error: fnErr } = await supabase.functions.invoke(
        'admin-auth',
        {
          body: { wallet: address, message, signature },
        },
      )
      if (fnErr) throw new Error(fnErr.message || 'Edge function failed')
      const actionLink = (data as { action_link?: string })?.action_link
      if (!actionLink) throw new Error('No session link returned')
      window.location.href = actionLink
    } catch (e) {
      setClaimError(getFriendlyTransactionError(e))
    } finally {
      setClaiming(false)
    }
  }, [address, signMessageAsync])

  const thClass =
    'border-b border-line px-3 py-2 text-left text-sm font-semibold text-fg-secondary'
  const tdClass = 'border-b border-line px-3 py-2 text-sm align-middle'

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-4">
        <button
          type="button"
          className="bg-accent-muted text-accent cursor-pointer rounded-md border border-[rgba(201,168,76,0.3)] px-4 py-2 text-sm font-medium hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          onClick={claimAdminSession}
          disabled={claiming || !address}
        >
          {claiming ? t('common.loading') : t('admin.claimAdminSession')}
        </button>
        <input
          type="text"
          className="border-line bg-surface-base text-fg placeholder:text-fg-muted min-w-[200px] rounded-md border px-3 py-2 text-[0.9375rem]"
          placeholder={t('admin.userFilterPlaceholder')}
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value)
            setLoading(true)
          }}
          aria-label={t('admin.userFilterPlaceholder')}
        />
      </div>

      {(error || claimError) && (
        <div className="text-fg-muted">{error || claimError}</div>
      )}

      {loading ? (
        <p className="text-fg-muted">{t('common.loading')}</p>
      ) : (
        <>
          <div className="overflow-x-auto text-sm max-md:text-[0.8125rem]">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className={thClass}>{t('admin.userWallet')}</th>
                  <th className={thClass}>{t('admin.userName')}</th>
                  <th className={thClass}>{t('admin.userEmail')}</th>
                  <th className={thClass}>{t('admin.userPhone')}</th>
                  <th className={thClass}>{t('admin.userCreated')}</th>
                  <th className={thClass} aria-label={t('admin.actions')} />
                </tr>
              </thead>
              <tbody>
                {profiles.map((p) => (
                  <tr key={p.id}>
                    <td
                      className={`${tdClass} max-w-[140px] truncate font-mono text-xs max-md:max-w-[100px]`}
                    >
                      {p.wallet_address}
                    </td>
                    <td className={tdClass}>{p.name ?? '—'}</td>
                    <td className={tdClass}>
                      {editingId === p.id ? (
                        <input
                          type="email"
                          className="border-line bg-surface-base text-fg min-w-[140px] rounded-sm border px-2 py-1 text-sm"
                          value={editEmail}
                          onChange={(e) => setEditEmail(e.target.value)}
                        />
                      ) : (
                        p.email
                      )}
                    </td>
                    <td className={tdClass}>
                      {editingId === p.id ? (
                        <input
                          type="text"
                          className="border-line bg-surface-base text-fg min-w-[140px] rounded-sm border px-2 py-1 text-sm"
                          value={editPhone}
                          onChange={(e) => setEditPhone(e.target.value)}
                          placeholder={t('profile.phonePlaceholder')}
                        />
                      ) : (
                        (p.phone ?? '—')
                      )}
                    </td>
                    <td className={`${tdClass} whitespace-nowrap`}>
                      {p.created_at
                        ? new Date(p.created_at).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className={tdClass}>
                      {editingId === p.id ? (
                        <span className="flex gap-1">
                          <button
                            type="button"
                            className="bg-accent-muted text-accent cursor-pointer rounded-sm border border-[rgba(201,168,76,0.3)] px-2 py-1 text-[0.8125rem]"
                            onClick={saveEdit}
                            disabled={saving}
                          >
                            {saving ? t('common.loading') : t('common.save')}
                          </button>
                          <button
                            type="button"
                            className="border-line text-fg-secondary cursor-pointer rounded-sm border bg-transparent px-2 py-1 text-[0.8125rem]"
                            onClick={cancelEdit}
                            disabled={saving}
                          >
                            {t('common.cancel')}
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="bg-accent-muted text-accent cursor-pointer rounded-sm border border-[rgba(201,168,76,0.3)] px-2 py-1 text-[0.8125rem]"
                          onClick={() => startEdit(p)}
                        >
                          {t('admin.userEdit')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center gap-4">
              <button
                type="button"
                className="border-line bg-surface-raised text-fg cursor-pointer rounded-md border px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                {t('admin.prev')}
              </button>
              <span className="text-fg-secondary text-sm">
                {t('admin.pageOf', { current: page + 1, total: totalPages })}
              </span>
              <button
                type="button"
                className="border-line bg-surface-raised text-fg cursor-pointer rounded-md border px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('admin.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
